import React, { useState, useEffect, useRef } from 'react';
import { ModalWrapper } from './ModalWrapper';
import { POPUP_STYLES } from '../../constants/popupStyles';
import { User, TaskType, InternalTaskItem, PatientMeeting, MeetingType, TaskHistoryItem, UpdateInternalTaskResponse } from '../../types/api';
import apiService from '../../services/api';
import { NotesList, NotesListRef } from '../ui/NotesList';
import { getMeetingTypeName } from '../../utils/meetingUtils';
import { formatRelativeDateTime } from '../../utils/dateUtils';
import { canScheduleMeeting } from '../../utils/permissions';

interface UpdateInternalTaskModalProps {
    isOpen: boolean;
    onClose: () => void;
    task: InternalTaskItem;
    users: User[];
    taskTypes?: TaskType[]; // Optional to avoid breaking existing usage
    meetingTypes?: MeetingType[]; // Optional to avoid breaking existing usage
    // Receives the task as the server saved it, so the caller can update its
    // list row straight away instead of waiting on a refetch.
    onSuccess?: (updatedTask?: UpdateInternalTaskResponse) => void;
    currentUserId?: number; // Current user ID for note editing
    onNavigate?: (screen: "calendar" | "patients" | "tasks" | "internal-tasks" | "patient-page" | "patient" | "users", patientId?: string | number, meetingId?: string | number) => void;
    onOpenEditPatient?: () => void;
    onOpenNewMeeting?: () => void;
    onOpenPSYSummary?: (meetingId: number) => void;
    onOpenCMSummary?: (meetingId: number) => void;
    currentUserType?: string;
    // Whether the caller is a clinic admin. An admin can close any task, so the
    // screen that owns the role check passes the answer in rather than the modal
    // re-deriving it from the user record.
    isAdmin?: boolean;
}

export const UpdateInternalTaskModal: React.FC<UpdateInternalTaskModalProps> = ({
    isOpen,
    onClose,
    task,
    users,
    taskTypes: propTaskTypes,
    meetingTypes: propMeetingTypes,
    onSuccess,
    currentUserId,
    onNavigate,
    onOpenEditPatient,
    onOpenNewMeeting,
    onOpenPSYSummary,
    onOpenCMSummary,
    currentUserType,
    isAdmin = false,
}) => {
    const [selectedTaskType, setSelectedTaskType] = useState<TaskType | null>(null);
    const [assignTo, setAssignTo] = useState<number[]>([]);
    const [priority, setPriority] = useState<string>('Low');
    const [description, setDescription] = useState<string>('');
    const [status, setStatus] = useState<string>('New');
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Tab state
    const [activeTab, setActiveTab] = useState<'task' | 'history'>('task');

    // History tab state
    const [taskHistory, setTaskHistory] = useState<TaskHistoryItem[]>([]);
    const [isHistoryLoading, setIsHistoryLoading] = useState(false);
    const [historySortColumn, setHistorySortColumn] = useState<string>('date');
    const [historySortDirection, setHistorySortDirection] = useState<'asc' | 'desc'>('desc');

    // Column resizing state for history table
    const [historyColumnWidths, setHistoryColumnWidths] = useState<Record<string, number>>(() => {
        try {
            const stored = localStorage.getItem('update-task-history-table-widths');
            if (stored) {
                return JSON.parse(stored);
            }
        } catch (error) {
            console.error('Failed to load history table column widths:', error);
        }
        return {};
    });
    const [historyResizingColumn, setHistoryResizingColumn] = useState<string | null>(null);
    const [historyStartX, setHistoryStartX] = useState<number>(0);
    const [historyStartWidth, setHistoryStartWidth] = useState<number>(0);

    // Meeting dropdown state
    const [meetings, setMeetings] = useState<PatientMeeting[]>([]);
    const [taskTypes, setTaskTypes] = useState<TaskType[]>(propTaskTypes || []);
    const [meetingTypes, setMeetingTypes] = useState<MeetingType[]>(propMeetingTypes || []);
    const [selectedMeetingId, setSelectedMeetingId] = useState<number | null>(null);
    const [isLoadingMeetings, setIsLoadingMeetings] = useState(false);

    // Searchable dropdown state
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    // Action dropdown state
    const [isActionDropdownOpen, setIsActionDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const userDropdownRef = useRef<HTMLDivElement>(null);

    // Delete confirmation state
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const notesListRef = useRef<NotesListRef>(null);

    // Track which tasks have been auto-updated to prevent multiple API calls
    const autoUpdatedTasksRef = useRef<Set<number>>(new Set());

    const token = localStorage.getItem('token') || '';

    const userNameMap = React.useMemo(() => {
        const map: Record<number, string> = {};
        users.forEach((u: any) => { map[Number(u.id)] = u.name || u.email; });
        return map;
    }, [users]);
    const getUserName = (userId: number) => userNameMap[userId] || 'Unknown';

    // Hebrew-name lookup (falls back to regular name/email) for Case Manager / Doctor display
    const userHebNameMap = React.useMemo(() => {
        const map: Record<number, string> = {};
        users.forEach((u: any) => { map[Number(u.id)] = u.heb_name || u.name || u.email; });
        return map;
    }, [users]);
    const getUserHebName = (userId: number) => userHebNameMap[userId] || 'Unknown';

    // Save column widths to localStorage
    useEffect(() => {
        try {
            localStorage.setItem('update-task-history-table-widths', JSON.stringify(historyColumnWidths));
        } catch (error) {
            console.error('Failed to save history table column widths:', error);
        }
    }, [historyColumnWidths]);

    // Filter to only active users
    const activeUsers = users.filter(user => user.active !== false);

    // Update taskTypes when prop changes
    useEffect(() => {
        if (propTaskTypes) {
            setTaskTypes(propTaskTypes);
        }
    }, [propTaskTypes]);

    // Update meetingTypes when prop changes
    useEffect(() => {
        if (propMeetingTypes) {
            setMeetingTypes(propMeetingTypes);
        }
    }, [propMeetingTypes]);

    // Fetch task types when modal opens (only if not provided via props)
    useEffect(() => {
        if (isOpen && propTaskTypes === undefined && token) {
            const fetchData = async () => {
                setIsLoading(true);
                setError(null);
                try {
                    const types = await apiService.getTaskTypes(token);
                    setTaskTypes(types);
                } catch (err: any) {
                    console.error('Failed to load data:', err);
                    setError('Failed to load data');
                } finally {
                    setIsLoading(false);
                }
            };
            fetchData();
        }
    }, [isOpen, propTaskTypes, token]);

    // Fetch meetings when task type has meeting_drop_down OR the task already carries a meeting_id
    // (so Meeting Summary can resolve PSY vs CM for any task type).
    useEffect(() => {
        if (isOpen && token && task?.patient_id && (selectedTaskType?.meeting_drop_down || task?.meeting_id)) {
            const fetchMeetings = async () => {
                setIsLoadingMeetings(true);
                try {
                    const patientMeetings = await apiService.getMeetingsForPatient(token, task.patient_id);
                    // Filter to only done meetings
                    const doneMeetings = patientMeetings.filter(m => m.status === 'done');
                    setMeetings(doneMeetings);
                } catch (err: any) {
                    console.error('Failed to load meetings:', err);
                } finally {
                    setIsLoadingMeetings(false);
                }
            };
            fetchMeetings();
        } else {
            setMeetings([]);
        }
    }, [isOpen, selectedTaskType, token, task?.patient_id, task?.meeting_id]);

    // Seed the form fields from a task record.
    const seedFormFromTask = React.useCallback((source: InternalTaskItem) => {
        // Keep the assignees exactly as the server has them, including ids that
        // aren't in the users list (a deleted user, or someone outside the
        // clinic the users list is scoped to). They render no chip, but they
        // must still go back on save: dropping one makes the backend read the
        // save as a reassignment and reset the status to "New", which silently
        // swallowed "Finish"/"Task Completed". The backend already tolerates
        // assignee ids it can't resolve.
        setAssignTo(source.assign_to || []);
        setPriority(source.priority || 'Low');
        setDescription(source.description || '');
        setSelectedMeetingId(source.meeting_id || null);
        setStatus(source.status || 'New');
    }, []);

    // Refs so the open effect below can run on open only (task id changing),
    // without re-seeding the form - and wiping the user's edits - every time the
    // parent's tasks query refetches and hands us a new task object.
    const taskRef = useRef(task);
    taskRef.current = task;
    const currentUserIdRef = useRef(currentUserId);
    currentUserIdRef.current = currentUserId;
    const onSuccessRef = useRef(onSuccess);
    onSuccessRef.current = onSuccess;
    const seedFormFromTaskRef = useRef(seedFormFromTask);
    seedFormFromTaskRef.current = seedFormFromTask;

    // Pre-populate form with task data when modal opens.
    //
    // The task we're handed is a row out of the parent's cached tasks list, which
    // has a 5-minute staleTime and is never invalidated when someone else changes
    // a task. So it is only good enough to render immediately: we re-fetch the
    // task and re-seed from the server copy before showing status buttons or
    // auto-advancing "New" -> "In Progress". Auto-advancing off the stale row is
    // what used to silently reopen tasks a second user had just completed.
    useEffect(() => {
        if (!isOpen || !task) return;
        const taskId = task.id;
        const patientId = task.patient_id;
        let cancelled = false;

        seedFormFromTaskRef.current(taskRef.current);

        if (!token) return;

        const autoAdvanceToInProgress = (current: InternalTaskItem) => {
            const userId = currentUserIdRef.current;
            const userIsAssigned = userId && (current.assign_to || []).includes(userId);
            if (!userIsAssigned || current.status !== 'New') return;
            // Only auto-update once per task per modal instance
            if (autoUpdatedTasksRef.current.has(current.id)) return;
            autoUpdatedTasksRef.current.add(current.id);
            setStatus('In Progress');

            const updatePayload: any = {
                token,
                id: current.id,
                patient_id: current.patient_id,
                type: current.type,
                assign_to: current.assign_to,
                priority: current.priority || 'Low',
                status: 'In Progress',
                description: current.description || '',
            };

            // Preserve meeting_id if it exists
            if (current.meeting_id) {
                updatePayload.meeting_id = current.meeting_id;
            }

            apiService.updateInternalTask(updatePayload).then(updated => {
                // Refresh the parent component to show the updated status
                onSuccessRef.current?.(updated);
            }).catch(err => {
                console.error('Failed to auto-update status:', err);
                // Remove from set if update failed so it can be retried
                autoUpdatedTasksRef.current.delete(current.id);
            });
        };

        apiService.getInternalTaskById({ token, id: taskId, patient_id: patientId })
            .then(fresh => {
                if (cancelled || !fresh) return;
                seedFormFromTaskRef.current(fresh);
                autoAdvanceToInProgress(fresh);
            })
            .catch(err => {
                // Deliberately no fallback to the cached row here: writing a status
                // we can't confirm is what caused the flapping in the first place.
                console.error('Failed to refresh task, skipping auto-update:', err);
            });

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, task?.id, task?.patient_id, token]);

    // Set task type after types are loaded (separate effect to avoid re-triggering auto-update)
    useEffect(() => {
        if (isOpen && task && taskTypes.length > 0) {
            const taskType = taskTypes.find(t => t.type === task.type);
            setSelectedTaskType(taskType || null);
        }
    }, [isOpen, task, taskTypes]);

    // Fetch task history when History tab is active
    useEffect(() => {
        if (isOpen && activeTab === 'history' && token && task) {
            const fetchHistory = async () => {
                setIsHistoryLoading(true);
                try {
                    const history = await apiService.getTaskHistory({
                        token,
                        task_id: task.id,
                    });
                    setTaskHistory(history);
                } catch (err: any) {
                    console.error('Failed to load task history:', err);
                } finally {
                    setIsHistoryLoading(false);
                }
            };
            fetchHistory();
        }
    }, [isOpen, activeTab, token, task]);

    // Reset form when modal closes
    useEffect(() => {
        if (!isOpen) {
            setSelectedTaskType(null);
            setAssignTo([]);
            setPriority('Low');
            setDescription('');
            setStatus('New');
            setError(null);
            setSearchQuery('');
            setIsDropdownOpen(false);
            setMeetings([]);
            setSelectedMeetingId(null);
            setActiveTab('task');
            setTaskHistory([]);
            // Clear auto-updated tasks when modal closes to free memory
            // Note: We don't clear the specific task being closed because
            // we want to remember it was auto-updated if the user reopens it
        }
    }, [isOpen]);

    // Column resize handlers for history table
    const handleHistoryResizeStart = (e: React.MouseEvent, column: string, currentWidth: number) => {
        e.preventDefault();
        e.stopPropagation();

        const element = (e.target as HTMLElement).closest('th');
        const actualWidth = element ? element.offsetWidth : currentWidth;

        setHistoryResizingColumn(column);
        setHistoryStartX(e.clientX);
        setHistoryStartWidth(actualWidth);
    };

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!historyResizingColumn) return;

            const diff = e.clientX - historyStartX;
            const newWidth = Math.max(50, historyStartWidth + diff);

            setHistoryColumnWidths(prev => ({
                ...prev,
                [historyResizingColumn]: newWidth
            }));
        };

        const handleMouseUp = () => {
            setHistoryResizingColumn(null);
        };

        if (historyResizingColumn) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);

            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [historyResizingColumn, historyStartX, historyStartWidth]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            // Check user dropdown
            if (userDropdownRef.current && !userDropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
            // Check action dropdown
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsActionDropdownOpen(false);
            }
        };

        if (isDropdownOpen || isActionDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isDropdownOpen, isActionDropdownOpen]);

    const handleTaskTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const typeId = parseInt(e.target.value, 10);
        const taskType = taskTypes.find(t => t.id === typeId) || null;
        setSelectedTaskType(taskType);
    };

    const toggleUserSelection = (userId: number) => {
        setAssignTo(prev => {
            if (prev.includes(userId)) {
                return prev.filter(id => id !== userId);
            } else {
                return [...prev, userId];
            }
        });
        setIsDropdownOpen(false);
        setSearchQuery('');
    };

    const removeUser = (userId: number) => {
        setAssignTo(prev => prev.filter(id => id !== userId));
    };

    // Filter and sort users: selected first, then filtered by search
    const getFilteredUsers = () => {
        const query = searchQuery.toLowerCase();
        const filtered = activeUsers.filter(user => {
            const name = user.name?.toLowerCase() || '';
            const email = user.email?.toLowerCase() || '';
            return name.includes(query) || email.includes(query);
        });

        // Sort: selected users first, then alphabetically
        return filtered.sort((a, b) => {
            const aSelected = assignTo.includes(Number(a.id));
            const bSelected = assignTo.includes(Number(b.id));

            if (aSelected && !bSelected) return -1;
            if (!aSelected && bSelected) return 1;

            const aName = a.name || a.email || '';
            const bName = b.name || b.email || '';
            return aName.localeCompare(bName);
        });
    };

    const getSelectedUsers = () => {
        return activeUsers.filter(user => assignTo.includes(Number(user.id)));
    };

    const handleSave = async (newStatus?: string) => {
        setError(null);

        await notesListRef.current?.submitPendingNote();

        // Validation
        if (!selectedTaskType) {
            setError('Please select a task type');
            return;
        }
        if (assignTo.length === 0) {
            setError('Please assign to at least one user');
            return;
        }
        if (!description.trim()) {
            setError('Please enter a description');
            return;
        }
        if (selectedTaskType.meeting_drop_down && !selectedMeetingId) {
            setError('Please select a meeting');
            return;
        }

        setIsSaving(true);
        try {
            const payload: any = {
                token,
                id: task.id,
                patient_id: task.patient_id,
                type: selectedTaskType.type,
                assign_to: assignTo,
                priority,
                status: newStatus || status,
                description: description.trim(),
            };

            // Add meeting_id if selected
            if (selectedMeetingId) {
                payload.meeting_id = selectedMeetingId;
            }

            const updated = await apiService.updateInternalTask(payload);

            if (onSuccess) {
                onSuccess(updated);
            }
            onClose();
        } catch (err: any) {
            console.error('Failed to update task:', err);
            setError(err.response?.data?.message || 'Failed to update task');
        } finally {
            setIsSaving(false);
        }
    };

    const handleSubmit = async () => {
        await handleSave();
    };

    const handleStatusChange = async (newStatus: string) => {
        await handleSave(newStatus);
    };

    const handleCancel = () => {
        onClose();
    };

    const handleDelete = async () => {
        setIsDeleting(true);
        try {
            await apiService.deleteInternalTask({
                token,
                id: task.id,
            });

            if (onSuccess) {
                onSuccess();
            }
            onClose();
        } catch (err: any) {
            console.error('Failed to delete task:', err);
            setError(err.response?.data?.message || 'Failed to delete task');
        } finally {
            setIsDeleting(false);
            setShowDeleteConfirmation(false);
        }
    };

    if (!isOpen) return null;

    const filteredUsers = getFilteredUsers();
    const selectedUsers = getSelectedUsers();

    // Determine which status buttons to show based on user role
    const isCreator = currentUserId === task.created_by;
    const isAssigned = currentUserId && assignTo.includes(currentUserId);

    // Button visibility logic
    let showWaitingButton = false;
    let showReopenButton = false;
    let showFinishButton = false;
    let showDoneButton = false;

    if (isCreator) {
        // Creator logic
        if (status === 'Done') {
            showReopenButton = true;
        } else {
            // Show "Done" button for creator in all statuses except "Done"
            showDoneButton = true;
            // Allow the creator to reopen a task the assignee marked "Finish"
            // (back to "In Progress"), alongside the "Task Completed" option.
            if (status === 'Finish') {
                showReopenButton = true;
            }
        }
    }

    if (isAssigned) {
        // Assigned user logic
        if (status === 'New' || status === 'In Progress') {
            showWaitingButton = true;
            showFinishButton = true;
        }
    }

    // An admin closes any task, including one they neither created nor were assigned
    // to. Only the closing action opens up: "Finish" stays the assignee's, and
    // reopening and deleting stay the creator's.
    if (isAdmin && status !== 'Done') {
        showDoneButton = true;
    }

    return (
        <ModalWrapper modalId="UpdateInternalTaskModal"
            isOpen={isOpen}
            onClose={handleCancel}
            title="Update Task"
            isLoading={isSaving}
            width="max-w-3xl"
            style={{ maxHeight: '90vh', overflowY: 'auto', paddingBottom: '30px' }}
            compactHeader={true}
        >
            {/* Tab Navigation */}
            <div className="flex items-center mb-4">
                <button
                    onClick={() => setActiveTab('task')}
                    className={`px-4 py-2 font-medium transition-all border ${activeTab === 'task'
                        ? 'bg-[#3A7380] text-white border-[#3A7380]'
                        : 'bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200'
                        }`}
                >
                    Task
                </button>
                <button
                    onClick={() => setActiveTab('history')}
                    className={`px-4 py-2 font-medium transition-all border ${activeTab === 'history'
                        ? 'bg-[#3A7380] text-white border-[#3A7380]'
                        : 'bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200'
                        }`}
                >
                    History
                </button>
                <div className="ml-6 flex flex-col gap-3 text-sm">
                    <div className="flex items-start gap-[72px]">
                        <div className="flex flex-col">
                            <span className="font-medium text-gray-500">Created by:</span>
                            <span className="font-bold text-gray-700">{getUserName(task.created_by)}</span>
                        </div>
                        <div className="flex flex-col">
                            <span className="font-medium text-gray-500">Created:</span>
                            <span className="font-bold text-gray-700">{formatRelativeDateTime(task.created_at)}</span>
                        </div>
                        {task.last_update && (
                            <div className="flex flex-col">
                                <span className="font-medium text-gray-500">Updated:</span>
                                <span className="font-bold text-gray-700">{formatRelativeDateTime(task.last_update)}</span>
                            </div>
                        )}
                    </div>
                    <div className="flex items-start gap-[72px]">
                        <div className="flex flex-col">
                            <span className="font-medium text-gray-500">Patient:</span>
                            <span className="font-bold text-gray-700">{`${task.first_name || ''} ${task.last_name || ''}`.trim() || '—'}</span>
                        </div>
                        <div className="flex flex-col">
                            <span className="font-medium text-gray-500">Case Manager:</span>
                            <span className="font-bold text-gray-700">{task.case_manager_id ? getUserHebName(task.case_manager_id) : '—'}</span>
                        </div>
                        <div className="flex flex-col">
                            <span className="font-medium text-gray-500">Doctor:</span>
                            <span className="font-bold text-gray-700">{task.doctor_id ? getUserHebName(task.doctor_id) : '—'}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Task Tab Content */}
            {activeTab === 'task' && (
                <div className="space-y-3 mb-6">

                    {/* Task Type with Action Dropdown Button */}
                    <div className="flex items-center gap-4">
                        <label className="text-sm font-medium text-gray-700 w-24 flex-shrink-0">
                            Task Type <span className="text-red-500">*</span>
                        </label>
                        <select
                            value={selectedTaskType?.id || ''}
                            onChange={handleTaskTypeChange}
                            className={`${POPUP_STYLES.input} flex-1`}
                            disabled={isLoading || isSaving}
                        >
                            <option value="">Select a task type</option>
                            {taskTypes.map(type => (
                                <option key={type.id} value={type.id}>
                                    {type.type}
                                </option>
                            ))}
                        </select>

                        {/* Action Split Button - Left executes action, Right opens dropdown.
                            Renders when the task type has a default_action, OR when a meeting is
                            selected (so Meeting Summary works regardless of task type). */}
                        {(() => {
                            const canSchedule = canScheduleMeeting(currentUserType);
                            // If a meeting is selected, Meeting Summary wins as the default action,
                            // regardless of the task type's own default_action.
                            const rawAction = selectedMeetingId
                                ? 'meeting_summary'
                                : (selectedTaskType?.default_action || null);
                            // If the user isn't allowed to schedule meetings, drop the
                            // set_meeting action and fall back to Patient Page as the default.
                            const effectiveAction = rawAction === 'set_meeting' && !canSchedule ? 'patient_page' : rawAction;
                            if (!effectiveAction) return null;
                            return (
                            <div className="relative" ref={dropdownRef}>
                                <div className="flex">
                                    {/* Main Action Button (Left Side) */}
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (effectiveAction === 'patient_page') {
                                                window.open(`patient?id=${task.patient_id}`, '_blank');
                                            } else if (effectiveAction === 'edit_patient') {
                                                onOpenEditPatient?.();
                                            } else if (effectiveAction === 'set_meeting') {
                                                onOpenNewMeeting?.();
                                            } else if (effectiveAction === 'meeting_summary') {
                                                if (selectedMeetingId && meetings.length > 0) {
                                                    const meeting = meetings.find(m => m.id === selectedMeetingId);
                                                    if (meeting && (meeting.meetingtype_id === 2 || meeting.meetingtype_id === 3)) {
                                                        onOpenPSYSummary?.(selectedMeetingId);
                                                    } else if (meeting) {
                                                        onOpenCMSummary?.(selectedMeetingId);
                                                    }
                                                }
                                            }
                                        }}
                                        disabled={isSaving}
                                        className="px-4 py-2 bg-[#4C6EF5] text-white rounded-l-lg shadow hover:bg-[#3B5ED6] transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium border-0 outline-none whitespace-nowrap"
                                        style={{ minWidth: '120px' }}
                                        title="Execute action"
                                    >
                                        {effectiveAction === 'patient_page' && 'Patient Page'}
                                        {effectiveAction === 'edit_patient' && 'Edit Patient'}
                                        {effectiveAction === 'set_meeting' && 'Set Meeting'}
                                        {effectiveAction === 'meeting_summary' && 'Meeting Summary'}
                                    </button>

                                    {/* Dropdown Arrow Button (Right Side) */}
                                    <button
                                        type="button"
                                        onClick={() => setIsActionDropdownOpen(!isActionDropdownOpen)}
                                        disabled={isSaving}
                                        className="px-3 py-2 bg-[#4C6EF5] text-white rounded-r-lg shadow hover:bg-[#3B5ED6] transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium border-l border-white/20 outline-none flex items-center justify-center"
                                        title="Show all actions"
                                        style={{ borderLeftWidth: '1px' }}
                                    >
                                        <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M1 1.5L6 6.5L11 1.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </button>
                                </div>

                                {/* Dropdown Menu */}
                                {isActionDropdownOpen && (
                                    <div className="absolute z-50 mt-1 w-full bg-white border border-[#B4D9D4] rounded-lg shadow-lg overflow-hidden">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                window.open(`patient?id=${task.patient_id}`, '_blank');
                                                setIsActionDropdownOpen(false);
                                            }}
                                            className="w-full px-4 py-2 text-left text-sm hover:bg-[#EBF5F5] transition-colors text-gray-700"
                                        >
                                            Patient Page
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                onOpenEditPatient?.();
                                                setIsActionDropdownOpen(false);
                                            }}
                                            className="w-full px-4 py-2 text-left text-sm hover:bg-[#EBF5F5] transition-colors text-gray-700"
                                        >
                                            Edit Patient
                                        </button>
                                        {canSchedule && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                onOpenNewMeeting?.();
                                                setIsActionDropdownOpen(false);
                                            }}
                                            className="w-full px-4 py-2 text-left text-sm hover:bg-[#EBF5F5] transition-colors text-gray-700"
                                        >
                                            Set Meeting
                                        </button>
                                        )}
                                        {selectedMeetingId && (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    if (selectedMeetingId && meetings.length > 0) {
                                                        const meeting = meetings.find(m => m.id === selectedMeetingId);
                                                        if (meeting && (meeting.meetingtype_id === 2 || meeting.meetingtype_id === 3)) {
                                                            onOpenPSYSummary?.(selectedMeetingId);
                                                        } else if (meeting) {
                                                            onOpenCMSummary?.(selectedMeetingId);
                                                        }
                                                    }
                                                    setIsActionDropdownOpen(false);
                                                }}
                                                className="w-full px-4 py-2 text-left text-sm hover:bg-[#EBF5F5] transition-colors text-gray-700"
                                            >
                                                Meeting Summary
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                            );
                        })()}
                    </div>

                    {/* Assign To - Searchable Dropdown */}
                    <div ref={userDropdownRef}>
                        <div className="flex items-start gap-4">
                            <label className="text-sm font-medium text-gray-700 w-24 flex-shrink-0 pt-2">
                                Assign To <span className="text-red-500">*</span>
                            </label>
                            <div className="flex-1">
                                {/* Selected Users Display */}
                                {selectedUsers.length > 0 && (
                                    <div className="mb-2 flex flex-wrap gap-2">
                                        {selectedUsers.map(user => (
                                            <div
                                                key={user.id}
                                                className="inline-flex items-center gap-1 px-2 py-1 bg-[#366A75] text-white rounded-md text-sm"
                                            >
                                                <span>{user.name || user.email}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => removeUser(Number(user.id))}
                                                    disabled={isSaving}
                                                    className="hover:text-red-200 transition-colors"
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Search Input */}
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        onFocus={() => setIsDropdownOpen(true)}
                                        placeholder="Search and select users..."
                                        className={POPUP_STYLES.input}
                                        disabled={isSaving}
                                    />

                                    {/* Dropdown */}
                                    {isDropdownOpen && (
                                        <div className="absolute z-10 w-full mt-1 bg-white border border-[#B4D9D4] rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                            {filteredUsers.length === 0 ? (
                                                <div className="px-4 py-3 text-sm text-gray-500">
                                                    No active users found
                                                </div>
                                            ) : (
                                                <div className="py-1">
                                                    {filteredUsers.map(user => {
                                                        const isSelected = assignTo.includes(Number(user.id));
                                                        return (
                                                            <button
                                                                data-ai-id="internal-task-assignee-option"
                                                                key={user.id}
                                                                type="button"
                                                                onClick={() => toggleUserSelection(Number(user.id))}
                                                                className={`w-full px-4 py-2 text-left text-sm hover:bg-[#EBF5F5] transition-colors flex items-center justify-between ${isSelected ? 'bg-[#EBF5F5] font-medium' : ''
                                                                    }`}
                                                            >
                                                                <span className="text-gray-700">
                                                                    {user.name || user.email}
                                                                </span>
                                                                {isSelected && (
                                                                    <span className="text-[#366A75]">✓</span>
                                                                )}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Meeting Dropdown - shown when task type requires it, or when the task already has a meeting_id */}
                    {(selectedTaskType?.meeting_drop_down || task?.meeting_id) && (
                        <div className="flex items-center gap-4">
                            <label className="text-sm font-medium text-gray-700 w-24 flex-shrink-0">
                                Meeting <span className="text-red-500">*</span>
                            </label>
                            <select
                                value={selectedMeetingId || ''}
                                onChange={(e) => setSelectedMeetingId(e.target.value ? Number(e.target.value) : null)}
                                className={`${POPUP_STYLES.input} flex-1`}
                                disabled={isSaving || isLoadingMeetings}
                            >
                                <option value="">
                                    {isLoadingMeetings ? 'Loading meetings...' : 'Select a meeting'}
                                </option>
                                {meetings.map(meeting => {
                                    // Format date as dd/mm/yyyy
                                    const meetingDate = new Date(meeting.start);
                                    const formattedDate = `${String(meetingDate.getDate()).padStart(2, '0')}/${String(meetingDate.getMonth() + 1).padStart(2, '0')}/${meetingDate.getFullYear()}`;

                                    // Get meeting type name using utility that handles re_intake
                                    const meetingTypeName = getMeetingTypeName(meeting, meetingTypes);

                                    return (
                                        <option key={meeting.id} value={meeting.id}>
                                            {formattedDate} {meetingTypeName}
                                        </option>
                                    );
                                })}
                            </select>
                        </div>
                    )}

                    {/* Priority and Status Row */}
                    <div className="flex items-center gap-4">
                        <label className="text-sm font-medium text-gray-700 w-24 flex-shrink-0">
                            Priority <span className="text-red-500">*</span>
                        </label>
                        <select
                            value={priority}
                            onChange={(e) => setPriority(e.target.value)}
                            className={`${POPUP_STYLES.input} flex-1`}
                            disabled={isSaving}
                        >
                            <option value="Low">Low</option>
                            <option value="Medium">Medium</option>
                            <option value="High">High</option>
                            <option value="Urgent">Urgent</option>
                        </select>
                        <label className="text-sm font-medium text-gray-700 w-24 flex-shrink-0 text-right">
                            Status
                        </label>
                        <input
                            type="text"
                            value={status}
                            readOnly
                            className={`${POPUP_STYLES.input} flex-1 bg-gray-100 cursor-not-allowed`}
                        />
                    </div>

                    {/* Description */}
                    <div className="flex items-start gap-4">
                        <label className="text-sm font-medium text-gray-700 w-24 flex-shrink-0 pt-2">
                            Description <span className="text-red-500">*</span>
                        </label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Add task description..."
                            className={`${POPUP_STYLES.input} resize-none flex-1`}
                            style={{
                                minHeight: '96px',
                                height: `${Math.max(96, description.split('\n').length * 24)}px`
                            }}
                            disabled={isSaving}
                        />
                    </div>

                    {/* Notes */}
                    <div className="flex items-start gap-4">
                        <label className="text-sm font-medium text-gray-700 w-24 flex-shrink-0 pt-1">
                            Notes
                        </label>
                        <div className="flex-1 min-w-0">
                            <NotesList
                                ref={notesListRef}
                                objectType="task"
                                objectId={task.id}
                                users={users}
                                currentUserId={currentUserId}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* History Tab Content */}
            {activeTab === 'history' && (
                <div className="mb-6">
                    <div className="flex-1 flex flex-col border border-gray-300 rounded-lg overflow-hidden" style={{ minHeight: '400px', maxHeight: '500px' }}>
                        {isHistoryLoading ? (
                            <div className="flex justify-center items-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400"></div>
                            </div>
                        ) : taskHistory.length === 0 ? (
                            <div className="text-center py-8 text-gray-500">No history available</div>
                        ) : (
                            <div className="flex-1 flex flex-col overflow-hidden">
                                {/* Header Table - Fixed at top */}
                                <table className="w-full border-collapse flex-shrink-0">
                                    <thead className="bg-gray-100">
                                        <tr className="flex bg-gray-100">
                                            <th
                                                className="border border-gray-300 px-3 py-2 text-left text-xs font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 select-none relative flex-shrink-0 truncate"
                                                style={historyColumnWidths['date'] ? { width: `${historyColumnWidths['date']}px`, minWidth: `${historyColumnWidths['date']}px`, maxWidth: `${historyColumnWidths['date']}px` } : { width: '150px' }}
                                                onClick={() => {
                                                    if (historySortColumn === 'date') {
                                                        setHistorySortDirection(historySortDirection === 'asc' ? 'desc' : 'asc');
                                                    } else {
                                                        setHistorySortColumn('date');
                                                        setHistorySortDirection('desc');
                                                    }
                                                }}
                                            >
                                                Date {historySortColumn === 'date' && (historySortDirection === 'asc' ? '↑' : '↓')}
                                                <div
                                                    onMouseDown={(e) => handleHistoryResizeStart(e, 'date', historyColumnWidths['date'] || 150)}
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-400 bg-gray-300"
                                                />
                                            </th>
                                            <th
                                                className="border border-gray-300 px-3 py-2 text-left text-xs font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 select-none relative flex-shrink-0 truncate"
                                                style={historyColumnWidths['user'] ? { width: `${historyColumnWidths['user']}px`, minWidth: `${historyColumnWidths['user']}px`, maxWidth: `${historyColumnWidths['user']}px` } : { width: '120px' }}
                                                onClick={() => {
                                                    if (historySortColumn === 'user_id') {
                                                        setHistorySortDirection(historySortDirection === 'asc' ? 'desc' : 'asc');
                                                    } else {
                                                        setHistorySortColumn('user_id');
                                                        setHistorySortDirection('asc');
                                                    }
                                                }}
                                            >
                                                User {historySortColumn === 'user_id' && (historySortDirection === 'asc' ? '↑' : '↓')}
                                                <div
                                                    onMouseDown={(e) => handleHistoryResizeStart(e, 'user', historyColumnWidths['user'] || 120)}
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-400 bg-gray-300"
                                                />
                                            </th>
                                            <th
                                                className="border border-gray-300 px-3 py-2 text-left text-xs font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 select-none relative flex-shrink-0 truncate"
                                                style={historyColumnWidths['action'] ? { width: `${historyColumnWidths['action']}px`, minWidth: `${historyColumnWidths['action']}px`, maxWidth: `${historyColumnWidths['action']}px` } : { width: '150px' }}
                                                onClick={() => {
                                                    if (historySortColumn === 'action') {
                                                        setHistorySortDirection(historySortDirection === 'asc' ? 'desc' : 'asc');
                                                    } else {
                                                        setHistorySortColumn('action');
                                                        setHistorySortDirection('asc');
                                                    }
                                                }}
                                            >
                                                Action {historySortColumn === 'action' && (historySortDirection === 'asc' ? '↑' : '↓')}
                                                <div
                                                    onMouseDown={(e) => handleHistoryResizeStart(e, 'action', historyColumnWidths['action'] || 150)}
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-400 bg-gray-300"
                                                />
                                            </th>
                                            <th
                                                className="border border-gray-300 px-3 py-2 text-left text-xs font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 select-none relative flex-shrink-0 truncate"
                                                style={historyColumnWidths['type'] ? { width: `${historyColumnWidths['type']}px`, minWidth: `${historyColumnWidths['type']}px`, maxWidth: `${historyColumnWidths['type']}px` } : { width: '100px' }}
                                                onClick={() => {
                                                    if (historySortColumn === 'type') {
                                                        setHistorySortDirection(historySortDirection === 'asc' ? 'desc' : 'asc');
                                                    } else {
                                                        setHistorySortColumn('type');
                                                        setHistorySortDirection('asc');
                                                    }
                                                }}
                                            >
                                                Type {historySortColumn === 'type' && (historySortDirection === 'asc' ? '↑' : '↓')}
                                                <div
                                                    onMouseDown={(e) => handleHistoryResizeStart(e, 'type', historyColumnWidths['type'] || 100)}
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-400 bg-gray-300"
                                                />
                                            </th>
                                            <th
                                                className="border border-gray-300 px-3 py-2 text-left text-xs font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 select-none relative flex-1"
                                                style={historyColumnWidths['notes'] ? { width: `${historyColumnWidths['notes']}px`, minWidth: `${historyColumnWidths['notes']}px` } : { minWidth: '150px' }}
                                                onClick={() => {
                                                    if (historySortColumn === 'notes') {
                                                        setHistorySortDirection(historySortDirection === 'asc' ? 'desc' : 'asc');
                                                    } else {
                                                        setHistorySortColumn('notes');
                                                        setHistorySortDirection('asc');
                                                    }
                                                }}
                                            >
                                                Notes {historySortColumn === 'notes' && (historySortDirection === 'asc' ? '↑' : '↓')}
                                                <div
                                                    onMouseDown={(e) => handleHistoryResizeStart(e, 'notes', historyColumnWidths['notes'] || 150)}
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-400 bg-gray-300"
                                                />
                                            </th>
                                        </tr>
                                    </thead>
                                </table>

                                {/* Body Table - Scrollable */}
                                <div className="flex-1 overflow-auto">
                                    <table className="w-full border-collapse">
                                        <tbody className="block">
                                            {(() => {
                                                let sortedData = [...taskHistory];
                                                sortedData.sort((a, b) => {
                                                    let aVal: any = a[historySortColumn as keyof TaskHistoryItem];
                                                    let bVal: any = b[historySortColumn as keyof TaskHistoryItem];

                                                    if (typeof aVal === 'string') {
                                                        aVal = aVal.toLowerCase();
                                                        bVal = (bVal as string).toLowerCase();
                                                    }

                                                    if (historySortDirection === 'asc') {
                                                        return aVal > bVal ? 1 : -1;
                                                    } else {
                                                        return aVal < bVal ? 1 : -1;
                                                    }
                                                });

                                                return sortedData.map((history) => (
                                                    <tr key={history.id} className="flex hover:bg-gray-50">
                                                        <td className="border border-gray-300 px-3 py-2 text-xs text-gray-700 flex-shrink-0 truncate" style={historyColumnWidths['date'] ? { width: `${historyColumnWidths['date']}px`, minWidth: `${historyColumnWidths['date']}px`, maxWidth: `${historyColumnWidths['date']}px` } : { width: '150px' }} title={(() => {
                                                            const date = new Date(history.date);
                                                            const day = String(date.getDate()).padStart(2, '0');
                                                            const month = String(date.getMonth() + 1).padStart(2, '0');
                                                            const year = date.getFullYear();
                                                            const hours = String(date.getHours()).padStart(2, '0');
                                                            const minutes = String(date.getMinutes()).padStart(2, '0');
                                                            return `${day}/${month}/${year} ${hours}:${minutes}`;
                                                        })()}>
                                                            {(() => {
                                                                const date = new Date(history.date);
                                                                const day = String(date.getDate()).padStart(2, '0');
                                                                const month = String(date.getMonth() + 1).padStart(2, '0');
                                                                const year = date.getFullYear();
                                                                const hours = String(date.getHours()).padStart(2, '0');
                                                                const minutes = String(date.getMinutes()).padStart(2, '0');
                                                                return `${day}/${month}/${year} ${hours}:${minutes}`;
                                                            })()}
                                                        </td>
                                                        <td className="border border-gray-300 px-3 py-2 text-xs text-gray-700 flex-shrink-0 truncate" style={historyColumnWidths['user'] ? { width: `${historyColumnWidths['user']}px`, minWidth: `${historyColumnWidths['user']}px`, maxWidth: `${historyColumnWidths['user']}px` } : { width: '120px' }} title={users.find(u => u.id === history.user_id)?.name || `User #${history.user_id}`}>
                                                            {users.find(u => u.id === history.user_id)?.name || `User #${history.user_id}`}
                                                        </td>
                                                        <td className="border border-gray-300 px-3 py-2 text-xs text-gray-700 flex-shrink-0 truncate" style={historyColumnWidths['action'] ? { width: `${historyColumnWidths['action']}px`, minWidth: `${historyColumnWidths['action']}px`, maxWidth: `${historyColumnWidths['action']}px` } : { width: '150px' }} title={history.action}>
                                                            {history.action}
                                                        </td>
                                                        <td className="border border-gray-300 px-3 py-2 text-xs text-gray-700 flex-shrink-0 truncate" style={historyColumnWidths['type'] ? { width: `${historyColumnWidths['type']}px`, minWidth: `${historyColumnWidths['type']}px`, maxWidth: `${historyColumnWidths['type']}px` } : { width: '100px' }} title={history.type}>
                                                            {history.type}
                                                        </td>
                                                        <td className="border border-gray-300 px-3 py-2 text-xs text-gray-700 flex-1 truncate" style={historyColumnWidths['notes'] ? { width: `${historyColumnWidths['notes']}px`, minWidth: `${historyColumnWidths['notes']}px` } : { minWidth: '150px' }} title={history.notes}>
                                                            {history.notes}
                                                        </td>
                                                    </tr>
                                                ));
                                            })()}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Error Message */}
            {error && (
                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
                    {error}
                </div>
            )}

            {/* Delete Confirmation Dialog */}
            {showDeleteConfirmation && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-lg p-6 max-w-sm mx-4">
                        <h3 className="text-lg font-semibold text-gray-800 mb-2">Delete Task</h3>
                        <p className="text-gray-600 mb-6">
                            Are you sure you want to delete this task? This action cannot be undone.
                        </p>
                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => setShowDeleteConfirmation(false)}
                                disabled={isDeleting}
                                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDelete}
                                disabled={isDeleting}
                                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                            >
                                {isDeleting ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Buttons - Order: Save, Waiting for Response, Re Open, Finish, Done, Cancel */}
            <div className="flex justify-center gap-2 flex-wrap">
                {/* Save Button - Always show */}
                <button
                    onClick={handleSubmit}
                    disabled={isSaving || isLoading}
                    className="px-4 py-2 bg-[#EBF5F5] text-[#3A7380] rounded-xl shadow-sm hover:bg-[#D0E7E5] transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium border-0 outline-none"
                >
                    {isSaving ? 'Saving...' : 'Save'}
                </button>

                {/* Waiting for Response Button */}
                {showWaitingButton && (
                    <button
                        onClick={() => handleStatusChange('Waiting for Response')}
                        disabled={isSaving || isLoading}
                        className="px-4 py-2 bg-[#EBF5F5] text-[#3A7380] rounded-xl shadow-sm hover:bg-[#D0E7E5] transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium border-0 outline-none whitespace-nowrap"
                    >
                        Waiting for Response
                    </button>
                )}

                {/* Re Open Button */}
                {showReopenButton && (
                    <button
                        onClick={() => handleStatusChange('In Progress')}
                        disabled={isSaving || isLoading}
                        className="px-4 py-2 bg-[#EBF5F5] text-[#3A7380] rounded-xl shadow-sm hover:bg-[#D0E7E5] transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium border-0 outline-none"
                    >
                        Re Open
                    </button>
                )}

                {/* Finish Button */}
                {showFinishButton && (
                    <button
                        onClick={() => handleStatusChange('Finish')}
                        disabled={isSaving || isLoading}
                        className="px-4 py-2 bg-[#EBF5F5] text-[#3A7380] rounded-xl shadow-sm hover:bg-[#D0E7E5] transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium border-0 outline-none"
                    >
                        Finish
                    </button>
                )}

                {/* Done Button */}
                {showDoneButton && (
                    <button
                        onClick={() => handleStatusChange('Done')}
                        disabled={isSaving || isLoading}
                        className="px-4 py-2 bg-[#EBF5F5] text-[#3A7380] rounded-xl shadow-sm hover:bg-[#D0E7E5] transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium border-0 outline-none"
                    >
                        Task Completed
                    </button>
                )}

                {/* Delete Button - Show only for creator when status is New */}
                {isCreator && status === 'New' && (
                    <button
                        onClick={() => setShowDeleteConfirmation(true)}
                        disabled={isSaving || isLoading || isDeleting}
                        className="px-4 py-2 bg-red-100 text-red-600 rounded-xl shadow-sm hover:bg-red-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium border-0 outline-none"
                    >
                        Delete Task
                    </button>
                )}

                {/* Cancel Button - Always show */}
                <button
                    onClick={handleCancel}
                    disabled={isSaving}
                    className="px-4 py-2 bg-[#EBF5F5] text-[#3A7380] rounded-xl shadow-sm hover:bg-[#D0E7E5] transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium border-0 outline-none"
                >
                    Cancel
                </button>
            </div>
        </ModalWrapper>
    );
};
